import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { CameraIcon, PhotoIcon, XMarkIcon, CheckCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface Bank {
  name: string;
  code: string;
  slug?: string;
}

interface User {
  id: number;
  uuid: string;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  role_name: string;
  approval_status: string;
  rejection_reason?: string;
  customer_type?: string;
  company_name?: string;
  company_registration_number?: string;
  company_address?: string;
  company_registration_doc_url?: string;
  company_logo_url?: string;
  rider_photo_url?: string;
  bank_code?: string;
  vehicle?: {
    vehicle_type: string;
    license_plate: string;
    capacity_kg: number;
    make: string;
    model?: string;
    year?: number;
    color: string;
    vehicle_photo_url?: string;
  };
  bank_info?: {
    bank_name: string;
    bank_code?: string;
    account_number: string;
    account_name: string;
  };
}

const PendingApprovalPage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    vehicle_type: '',
    license_plate: '',
    capacity_kg: '',
    make: '',
    model: '',
    year: '',
    color: '',
    bank_name: '',
    bank_code: '',
    account_number: '',
    account_name: '',
    password: '',
    company_name: '',
    company_registration_number: '',
    company_address: ''
  });

  // Bank dropdown state
  const [banks, setBanks] = useState<Bank[]>([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [verifyingAccount, setVerifyingAccount] = useState(false);
  const bankDropdownRef = useRef<HTMLDivElement>(null);

  // Photo upload state
  const [riderPhoto, setRiderPhoto] = useState<File | null>(null);
  const [riderPhotoPreview, setRiderPhotoPreview] = useState<string | null>(null);
  const [vehiclePhoto, setVehiclePhoto] = useState<File | null>(null);
  const [vehiclePhotoPreview, setVehiclePhotoPreview] = useState<string | null>(null);
  const riderPhotoRef = useRef<HTMLInputElement>(null);
  const riderCameraRef = useRef<HTMLInputElement>(null);
  const vehiclePhotoRef = useRef<HTMLInputElement>(null);
  const vehicleCameraRef = useRef<HTMLInputElement>(null);

  // Corporate document state
  const [registrationDocFile, setRegistrationDocFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const regDocInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Fetch banks on component mount
  useEffect(() => {
    const fetchBanks = async () => {
      setBanksLoading(true);
      try {
        const response = await api.get('/banks');
        setBanks(response.data.banks || []);
      } catch (err) {
        console.error('Failed to fetch banks:', err);
      } finally {
        setBanksLoading(false);
      }
    };
    fetchBanks();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bankDropdownRef.current && !bankDropdownRef.current.contains(event.target as Node)) {
        setShowBankDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      const bankName = parsedUser.bank_info?.bank_name || '';
      const bankCode = parsedUser.bank_info?.bank_code || parsedUser.bank_code || '';
      
      setFormData({
        vehicle_type: parsedUser.vehicle?.vehicle_type || '',
        license_plate: parsedUser.vehicle?.license_plate || '',
        capacity_kg: parsedUser.vehicle?.capacity_kg?.toString() || '',
        make: parsedUser.vehicle?.make || '',
        model: parsedUser.vehicle?.model || '',
        year: parsedUser.vehicle?.year?.toString() || '',
        color: parsedUser.vehicle?.color || '',
        bank_name: bankName,
        bank_code: bankCode,
        account_number: parsedUser.bank_info?.account_number || '',
        account_name: parsedUser.bank_info?.account_name || '',
        password: '',
        company_name: parsedUser.company_name || '',
        company_registration_number: parsedUser.company_registration_number || '',
        company_address: parsedUser.company_address || ''
      });
      
      // Set bank search text
      setBankSearch(bankName);
      
      // Set existing photo previews
      if (parsedUser.rider_photo_url) {
        setRiderPhotoPreview(parsedUser.rider_photo_url);
      }
      if (parsedUser.vehicle?.vehicle_photo_url) {
        setVehiclePhotoPreview(parsedUser.vehicle.vehicle_photo_url);
      }
    }
  }, []);

  // Set selected bank when banks load
  useEffect(() => {
    if (banks.length > 0 && formData.bank_code) {
      const matchedBank = banks.find(b => b.code === formData.bank_code);
      if (matchedBank) {
        setSelectedBank(matchedBank);
        setBankSearch(matchedBank.name);
      }
    }
  }, [banks, formData.bank_code]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Filter banks based on search
  const filteredBanks = banks.filter(bank => 
    bank.name.toLowerCase().includes(bankSearch.toLowerCase())
  );

  // Handle bank selection
  const handleBankSelect = (bank: Bank) => {
    setSelectedBank(bank);
    setFormData(prev => ({ ...prev, bank_name: bank.name, bank_code: bank.code }));
    setBankSearch(bank.name);
    setShowBankDropdown(false);
    
    // Auto-verify account if account number is already entered
    if (formData.account_number.length === 10) {
      verifyBankAccount(formData.account_number, bank.code);
    }
  };

  // Verify bank account
  const verifyBankAccount = async (accountNumber: string, bankCode: string) => {
    if (accountNumber.length !== 10 || !bankCode) return;
    
    setVerifyingAccount(true);
    try {
      const response = await api.post('/banks/resolve', null, {
        params: { account_number: accountNumber, bank_code: bankCode }
      });
      if (response.data.account_name) {
        setFormData(prev => ({ ...prev, account_name: response.data.account_name }));
      }
    } catch (err) {
      console.error('Account verification failed:', err);
    } finally {
      setVerifyingAccount(false);
    }
  };

  // Handle account number change with auto-verification
  const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, account_number: value }));
    
    if (value.length === 10 && selectedBank) {
      verifyBankAccount(value, selectedBank.code);
    }
  };

  // Photo handling
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'rider' | 'vehicle') => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'rider') {
          setRiderPhoto(file);
          setRiderPhotoPreview(reader.result as string);
        } else {
          setVehiclePhoto(file);
          setVehiclePhotoPreview(reader.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const clearPhoto = (type: 'rider' | 'vehicle') => {
    if (type === 'rider') {
      setRiderPhoto(null);
      setRiderPhotoPreview(user?.rider_photo_url || null);
      if (riderPhotoRef.current) riderPhotoRef.current.value = '';
      if (riderCameraRef.current) riderCameraRef.current.value = '';
    } else {
      setVehiclePhoto(null);
      setVehiclePhotoPreview(user?.vehicle?.vehicle_photo_url || null);
      if (vehiclePhotoRef.current) vehiclePhotoRef.current.value = '';
      if (vehicleCameraRef.current) vehicleCameraRef.current.value = '';
    }
  };

  const isCorporateCustomer = user?.customer_type === 'corporate';
  const hasExistingDocs = user?.company_registration_doc_url && user?.company_logo_url;
  const needsDocuments = !hasExistingDocs;

  const handleCorporateUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (needsDocuments && (!registrationDocFile || !logoFile)) {
      setError('Both CAC Registration Certificate and Company Logo are required. Please upload both documents.');
      setLoading(false);
      return;
    }

    if (!user?.company_registration_doc_url && !registrationDocFile) {
      setError('CAC Registration Certificate is required. Please upload the document.');
      setLoading(false);
      return;
    }

    if (!user?.company_logo_url && !logoFile) {
      setError('Company Logo is required. Please upload the logo.');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch('/auth/corporate/update-registration', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          company_name: formData.company_name,
          company_registration_number: formData.company_registration_number,
          company_address: formData.company_address
        })
      });

      const data = await response.json();

      if (response.ok) {
        if (registrationDocFile || logoFile) {
          const uploadFormData = new FormData();
          if (registrationDocFile) {
            uploadFormData.append('registration_doc', registrationDocFile);
          }
          if (logoFile) {
            uploadFormData.append('company_logo', logoFile);
          }

          const uploadResponse = await fetch('/auth/corporate/upload-documents', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: uploadFormData
          });

          if (!uploadResponse.ok) {
            const uploadError = await uploadResponse.json();
            setError(uploadError.detail || 'Failed to upload documents');
            setLoading(false);
            return;
          }
        }

        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        
        window.dispatchEvent(new Event('storage'));
        
        setSuccess('Registration updated successfully! Your application has been resubmitted for admin review.');
        setIsEditing(false);
        setRegistrationDocFile(null);
        setLogoFile(null);
        
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setError(data.detail || 'Failed to update registration');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDispatcherUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!selectedBank || !formData.bank_code) {
      setError('Please select your bank from the dropdown');
      setLoading(false);
      return;
    }

    if (!formData.account_number || formData.account_number.length !== 10) {
      setError('Please provide a valid 10-digit account number');
      setLoading(false);
      return;
    }

    if (!formData.account_name) {
      setError('Please provide your account name');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      
      // Upload photos first if provided
      let riderPhotoUrl = null;
      let vehiclePhotoUrl = null;
      
      if (riderPhoto || vehiclePhoto) {
        const photoFormData = new FormData();
        if (riderPhoto) {
          photoFormData.append('rider_photo', riderPhoto);
        }
        if (vehiclePhoto) {
          photoFormData.append('vehicle_photo', vehiclePhoto);
        }
        
        const photoResponse = await fetch('/auth/dispatcher/upload-photos', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: photoFormData
        });
        
        if (photoResponse.ok) {
          const photoData = await photoResponse.json();
          riderPhotoUrl = photoData.rider_photo_url;
          vehiclePhotoUrl = photoData.vehicle_photo_url;
        } else {
          const photoError = await photoResponse.json();
          setError(photoError.detail || 'Failed to upload photos');
          setLoading(false);
          return;
        }
      }
      
      // Update registration
      const response = await fetch('/auth/dispatcher/update-registration', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          vehicle_type: formData.vehicle_type,
          license_plate: formData.license_plate,
          capacity_kg: parseFloat(formData.capacity_kg),
          make: formData.make,
          model: formData.model || null,
          year: formData.year ? parseInt(formData.year) : null,
          color: formData.color,
          bank_name: formData.bank_name,
          bank_code: formData.bank_code,
          account_number: formData.account_number,
          account_name: formData.account_name,
          password: formData.password || null,
          rider_photo_url: riderPhotoUrl,
          vehicle_photo_url: vehiclePhotoUrl
        })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        
        window.dispatchEvent(new Event('storage'));
        
        setSuccess('Registration updated successfully! Your application has been resubmitted for admin review.');
        setIsEditing(false);
        setFormData(prev => ({ ...prev, password: '' }));
        
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update registration');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-volexx-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-poppins">Loading...</p>
        </div>
      </div>
    );
  }

  const vehicleTypes = [
    'Bicycle', 'Motorbike', 'Sedan', 'SUV', 'Pickup Truck',
    'Medium Van', '12-Wheeler', '18-Wheeler', 'Motorboat',
    'Light Plane', 'Helicopter', 'Cargo Plane'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img src="/volexx-logo.png" alt="Volexx" className="h-10" />
              <span className="text-xl font-bold text-volexx-blue-500 font-poppins">Volexx Logistics</span>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-volexx-blue-500 font-poppins transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className={`bg-white rounded-volexx shadow-sm p-8 mb-8 border-l-4 ${
          user.approval_status === 'rejected' ? 'border-red-500' : isCorporateCustomer ? 'border-orange-500' : 'border-volexx-blue-500'
        }`}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {isCorporateCustomer && (
                  <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-semibold">
                    Corporate Account
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-gray-900 font-poppins mb-2">
                {user.approval_status === 'pending' ? 'Application Under Review' : 'Application Requires Update'}
              </h1>
              <p className="text-gray-600 font-poppins">
                Welcome back, {isCorporateCustomer && user.company_name ? user.company_name : user.first_name}!
                {isCorporateCustomer && user.company_name && (
                  <span className="block text-sm mt-1">Contact: {user.first_name} {user.last_name}</span>
                )}
              </p>
            </div>
            <div className={`px-4 py-2 rounded-full font-semibold font-poppins ${
              user.approval_status === 'pending' 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {user.approval_status === 'pending' ? 'Pending Review' : 'Rejected'}
            </div>
          </div>

          {user.approval_status === 'pending' && (
            <>
              {isCorporateCustomer && needsDocuments && (
                <div className="bg-red-50 border border-red-200 rounded-volexx p-4 mb-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <h3 className="font-semibold text-red-800 font-poppins">Documents Required</h3>
                  </div>
                  <p className="text-sm text-red-700 font-poppins mb-3">
                    Your registration is incomplete. Please upload your CAC Registration Certificate and Company Logo to complete your application.
                  </p>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded-volexx font-poppins transition-colors"
                  >
                    Upload Required Documents
                  </button>
                </div>
              )}
              {(!isCorporateCustomer || !needsDocuments) && (
                <div className={`rounded-volexx p-4 ${isCorporateCustomer ? 'bg-orange-50 border border-orange-200' : 'bg-blue-50 border border-blue-200'}`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <svg className={`w-5 h-5 ${isCorporateCustomer ? 'text-orange-600' : 'text-blue-600'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <h3 className={`font-semibold font-poppins ${isCorporateCustomer ? 'text-orange-800' : 'text-blue-800'}`}>
                      Your {isCorporateCustomer ? 'Corporate ' : ''}Application is Being Reviewed
                    </h3>
                  </div>
                  <p className={`text-sm font-poppins ${isCorporateCustomer ? 'text-orange-700' : 'text-blue-700'}`}>
                    {isCorporateCustomer 
                      ? 'Our admin team is reviewing your corporate account application. You\'ll receive an email once your company has been verified. This typically takes 1-2 business days.'
                      : 'Our admin team is currently reviewing your dispatcher application. You\'ll receive a notification once your account has been approved. This typically takes 1-2 business days.'}
                  </p>
                </div>
              )}
            </>
          )}

          {user.approval_status === 'rejected' && user.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded-volexx p-4">
              <div className="flex items-center space-x-2 mb-2">
                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <h3 className="font-semibold text-red-800 font-poppins">Admin Feedback</h3>
              </div>
              <p className="text-red-700 font-poppins mb-4">{user.rejection_reason}</p>
              <button
                onClick={() => setIsEditing(true)}
                className="bg-volexx-orange-500 hover:bg-volexx-orange-600 text-white font-semibold py-2 px-6 rounded-volexx font-poppins transition-colors"
              >
                Update & Resubmit Application
              </button>
            </div>
          )}
        </div>

        {isEditing && isCorporateCustomer && (
          <div className="bg-white rounded-volexx shadow-sm p-8">
            <h2 className="text-xl font-bold text-gray-900 font-poppins mb-6">Update Corporate Registration</h2>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-volexx font-poppins">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-volexx font-poppins">
                {success}
              </div>
            )}

            <form onSubmit={handleCorporateUpdateSubmit} className="space-y-6">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Company Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-orange-500 font-poppins"
                      value={formData.company_name}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      CAC Registration Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="company_registration_number"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-orange-500 font-poppins"
                      value={formData.company_registration_number}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Business Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="company_address"
                      required
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-orange-500 font-poppins"
                      value={formData.company_address}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 font-poppins">
                  Company Documents {needsDocuments ? <span className="text-red-500">(Required)</span> : '(Update if needed)'}
                </h3>
                {needsDocuments && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-700 text-sm font-poppins">
                      Both CAC Registration Certificate and Company Logo are required to complete your corporate registration.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      CAC Registration Certificate {!user?.company_registration_doc_url && <span className="text-red-500">*</span>}
                    </label>
                    <div
                      onClick={() => regDocInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
                        ${registrationDocFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50'}`}
                    >
                      <input
                        ref={regDocInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => setRegistrationDocFile(e.target.files?.[0] || null)}
                      />
                      {registrationDocFile ? (
                        <div className="text-green-600">
                          <p className="font-medium text-sm">{registrationDocFile.name}</p>
                          <p className="text-xs text-gray-500">Click to change</p>
                        </div>
                      ) : (
                        <>
                          <span className="text-2xl text-gray-400">📋</span>
                          <p className="mt-1 text-sm text-gray-600">Upload new certificate</p>
                        </>
                      )}
                    </div>
                    {user.company_registration_doc_url && !registrationDocFile && (
                      <p className="text-xs text-green-600 mt-1">Current document uploaded</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Company Logo {!user?.company_logo_url && <span className="text-red-500">*</span>}
                    </label>
                    <div
                      onClick={() => logoInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
                        ${logoFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50'}`}
                    >
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.svg"
                        className="hidden"
                        onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                      />
                      {logoFile ? (
                        <div className="text-green-600">
                          <p className="font-medium text-sm">{logoFile.name}</p>
                          <p className="text-xs text-gray-500">Click to change</p>
                        </div>
                      ) : (
                        <>
                          <span className="text-2xl text-gray-400">🖼️</span>
                          <p className="mt-1 text-sm text-gray-600">Upload new logo</p>
                        </>
                      )}
                    </div>
                    {user.company_logo_url && !logoFile && (
                      <p className="text-xs text-green-600 mt-1">Current logo uploaded</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-volexx-orange-500 hover:bg-volexx-orange-600 text-white font-bold py-3 px-6 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-orange-500 font-poppins transition-all disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update & Resubmit'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 px-6 rounded-volexx focus:outline-none font-poppins transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {isEditing && !isCorporateCustomer && (
          <div className="bg-white rounded-volexx shadow-sm p-8">
            <h2 className="text-xl font-bold text-gray-900 font-poppins mb-6">Update Your Registration</h2>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-volexx font-poppins">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-volexx font-poppins">
                {success}
              </div>
            )}

            <form onSubmit={handleDispatcherUpdateSubmit} className="space-y-6">
              {/* Photo Upload Section */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Photos</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Rider Photo */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Your Photo
                    </label>
                    {riderPhotoPreview ? (
                      <div className="relative">
                        <img
                          src={riderPhotoPreview}
                          alt="Rider preview"
                          className="w-full h-48 object-cover rounded-volexx border-2 border-green-300"
                        />
                        <button
                          type="button"
                          onClick={() => clearPhoto('rider')}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                        <div className="absolute bottom-2 left-2 bg-green-500 text-white rounded-full p-1">
                          <CheckCircleIcon className="h-5 w-5" />
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-volexx p-4 text-center">
                        <div className="space-y-2">
                          <div className="flex justify-center space-x-3">
                            <button
                              type="button"
                              onClick={() => riderCameraRef.current?.click()}
                              className="flex items-center px-4 py-2 bg-volexx-blue-500 text-white rounded-volexx hover:bg-volexx-blue-600 transition-colors"
                            >
                              <CameraIcon className="h-5 w-5 mr-2" />
                              Take Photo
                            </button>
                            <button
                              type="button"
                              onClick={() => riderPhotoRef.current?.click()}
                              className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-volexx hover:bg-gray-600 transition-colors"
                            >
                              <PhotoIcon className="h-5 w-5 mr-2" />
                              Upload
                            </button>
                          </div>
                          <p className="text-xs text-gray-500">Take a clear photo of yourself</p>
                        </div>
                        <input
                          ref={riderCameraRef}
                          type="file"
                          accept="image/*"
                          capture="user"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, 'rider')}
                        />
                        <input
                          ref={riderPhotoRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, 'rider')}
                        />
                      </div>
                    )}
                  </div>

                  {/* Vehicle Photo */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Vehicle Photo
                    </label>
                    {vehiclePhotoPreview ? (
                      <div className="relative">
                        <img
                          src={vehiclePhotoPreview}
                          alt="Vehicle preview"
                          className="w-full h-48 object-cover rounded-volexx border-2 border-green-300"
                        />
                        <button
                          type="button"
                          onClick={() => clearPhoto('vehicle')}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                        <div className="absolute bottom-2 left-2 bg-green-500 text-white rounded-full p-1">
                          <CheckCircleIcon className="h-5 w-5" />
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-volexx p-4 text-center">
                        <div className="space-y-2">
                          <div className="flex justify-center space-x-3">
                            <button
                              type="button"
                              onClick={() => vehicleCameraRef.current?.click()}
                              className="flex items-center px-4 py-2 bg-volexx-blue-500 text-white rounded-volexx hover:bg-volexx-blue-600 transition-colors"
                            >
                              <CameraIcon className="h-5 w-5 mr-2" />
                              Take Photo
                            </button>
                            <button
                              type="button"
                              onClick={() => vehiclePhotoRef.current?.click()}
                              className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-volexx hover:bg-gray-600 transition-colors"
                            >
                              <PhotoIcon className="h-5 w-5 mr-2" />
                              Upload
                            </button>
                          </div>
                          <p className="text-xs text-gray-500">Take a clear photo of your vehicle</p>
                        </div>
                        <input
                          ref={vehicleCameraRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, 'vehicle')}
                        />
                        <input
                          ref={vehiclePhotoRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, 'vehicle')}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Vehicle Information */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Vehicle Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Vehicle Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="vehicle_type"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                      value={formData.vehicle_type}
                      onChange={handleChange}
                    >
                      <option value="">Select vehicle type</option>
                      {vehicleTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      License Plate <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="license_plate"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                      value={formData.license_plate}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Capacity (kg) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="capacity_kg"
                      step="0.01"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                      value={formData.capacity_kg}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Make <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="make"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                      value={formData.make}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">Model</label>
                    <input
                      type="text"
                      name="model"
                      className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                      value={formData.model}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">Year</label>
                    <input
                      type="number"
                      name="year"
                      className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                      value={formData.year}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Color <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="color"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                      value={formData.color}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {/* Bank Information */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Bank Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Searchable Bank Dropdown */}
                  <div className="md:col-span-2" ref={bankDropdownRef}>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Bank Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder={banksLoading ? "Loading banks..." : "Search for your bank..."}
                          className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                          value={bankSearch}
                          onChange={(e) => {
                            setBankSearch(e.target.value);
                            setShowBankDropdown(true);
                            if (selectedBank && e.target.value !== selectedBank.name) {
                              setSelectedBank(null);
                              setFormData(prev => ({ ...prev, bank_code: '', bank_name: '' }));
                            }
                          }}
                          onFocus={() => setShowBankDropdown(true)}
                          disabled={banksLoading}
                        />
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        {selectedBank && (
                          <CheckCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
                        )}
                      </div>
                      
                      {showBankDropdown && !banksLoading && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-volexx shadow-lg max-h-60 overflow-y-auto">
                          {filteredBanks.length > 0 ? (
                            filteredBanks.map((bank) => (
                              <div
                                key={bank.code}
                                className={`px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors ${
                                  selectedBank?.code === bank.code ? 'bg-blue-100' : ''
                                }`}
                                onClick={() => handleBankSelect(bank)}
                              >
                                <div className="font-medium text-gray-900">{bank.name}</div>
                                <div className="text-xs text-gray-500">Code: {bank.code}</div>
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-gray-500 text-center">
                              No banks found matching "{bankSearch}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {selectedBank && (
                      <p className="mt-1 text-xs text-green-600">Selected: {selectedBank.name} (Code: {selectedBank.code})</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Account Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="account_number"
                        required
                        maxLength={10}
                        placeholder="Enter 10-digit account number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                        value={formData.account_number}
                        onChange={handleAccountNumberChange}
                      />
                      {verifyingAccount && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-volexx-blue-500"></div>
                        </div>
                      )}
                    </div>
                    {formData.account_number.length > 0 && formData.account_number.length < 10 && (
                      <p className="mt-1 text-xs text-gray-500">{10 - formData.account_number.length} more digits needed</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Account Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="account_name"
                        required
                        placeholder="Will auto-fill when verified"
                        className={`w-full px-4 py-3 border rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins ${
                          formData.account_name ? 'border-green-300 bg-green-50' : 'border-gray-300'
                        }`}
                        value={formData.account_name}
                        onChange={handleChange}
                        readOnly={verifyingAccount}
                      />
                      {formData.account_name && (
                        <CheckCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
                      )}
                    </div>
                    {formData.account_name && (
                      <p className="mt-1 text-xs text-green-600">Account verified successfully</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Password Update */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Update Password (Optional)</h3>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                    New Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    placeholder="Leave blank to keep current password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-volexx-orange-500 hover:bg-volexx-orange-600 text-white font-bold py-3 px-6 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-orange-500 font-poppins transition-all disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update & Resubmit'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 px-6 rounded-volexx focus:outline-none font-poppins transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {!isEditing && isCorporateCustomer && (
          <div className="bg-white rounded-volexx shadow-sm p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 font-poppins">Current Company Details</h2>
              {user.approval_status === 'rejected' && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-orange-500 hover:text-orange-600 font-semibold font-poppins transition-colors"
                >
                  Edit Details
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Company Name</h3>
                <p className="text-gray-900 font-poppins">{user.company_name || '-'}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">CAC Registration Number</h3>
                <p className="text-gray-900 font-poppins">{user.company_registration_number || '-'}</p>
              </div>
              <div className="md:col-span-2">
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Business Address</h3>
                <p className="text-gray-900 font-poppins">{user.company_address || '-'}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Registration Document</h3>
                <p className="text-gray-900 font-poppins">
                  {user.company_registration_doc_url ? (
                    <span className="text-green-600">Uploaded</span>
                  ) : (
                    <span className="text-gray-400">Not uploaded</span>
                  )}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Company Logo</h3>
                <p className="text-gray-900 font-poppins">
                  {user.company_logo_url ? (
                    <span className="text-green-600">Uploaded</span>
                  ) : (
                    <span className="text-gray-400">Not uploaded</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {!isEditing && !isCorporateCustomer && user.vehicle && (
          <div className="bg-white rounded-volexx shadow-sm p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 font-poppins">Current Registration Details</h2>
              {user.approval_status === 'rejected' && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-volexx-blue-500 hover:text-volexx-blue-600 font-semibold font-poppins transition-colors"
                >
                  Edit Details
                </button>
              )}
            </div>

            {/* Photos Display */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Your Photo</h3>
                {user.rider_photo_url ? (
                  <img src={user.rider_photo_url} alt="Rider" className="w-32 h-32 object-cover rounded-volexx border" />
                ) : (
                  <p className="text-gray-400">Not uploaded</p>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Vehicle Photo</h3>
                {user.vehicle?.vehicle_photo_url ? (
                  <img src={user.vehicle.vehicle_photo_url} alt="Vehicle" className="w-32 h-32 object-cover rounded-volexx border" />
                ) : (
                  <p className="text-gray-400">Not uploaded</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Vehicle Type</h3>
                <p className="text-gray-900 font-poppins">{user.vehicle.vehicle_type}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">License Plate</h3>
                <p className="text-gray-900 font-poppins">{user.vehicle.license_plate}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Capacity</h3>
                <p className="text-gray-900 font-poppins">{user.vehicle.capacity_kg} kg</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Make</h3>
                <p className="text-gray-900 font-poppins">{user.vehicle.make}</p>
              </div>
              {user.vehicle.model && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Model</h3>
                  <p className="text-gray-900 font-poppins">{user.vehicle.model}</p>
                </div>
              )}
              {user.vehicle.color && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Color</h3>
                  <p className="text-gray-900 font-poppins">{user.vehicle.color}</p>
                </div>
              )}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Bank</h3>
                <p className="text-gray-900 font-poppins">{user.bank_info?.bank_name || '-'}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 font-poppins">Account Number</h3>
                <p className="text-gray-900 font-poppins">{user.bank_info?.account_number || '-'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingApprovalPage;
